import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturesService } from '../../../js/api';
import { useDocumentTitle } from '../../../js/app/hooks/use-document-title';
import { SignatureSignBody } from '../components/signature-sign-body';
import { Spinner } from '../components/spinner';

/** Full-page shell shared by every state below. */
function SigningShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw:p-4 tw:max-w-3xl tw:lg:max-w-5xl tw:xl:max-w-6xl tw:mx-auto tw:w-full">
      <div className="tw:bg-white tw:rounded-xl tw:p-6 tw:sm:p-8 tw:border tw:border-zinc-200">
        {children}
      </div>
    </div>
  );
}

function CenteredMessage({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center tw:py-6">
      <i className={`${icon} tw:text-4xl tw:text-brand-green-0`} />
      <h2 className="tw:text-xl tw:font-semibold tw:text-zinc-900">{title}</h2>
      <p className="tw:max-w-md tw:text-zinc-600">{body}</p>
    </div>
  );
}

/**
 * Public (logged-out) signing page reached via a directed magic link. The
 * token authorizes access to exactly one participant/document; the signing
 * experience is the same shared body used by the in-app dialog.
 */
export function PublicDocumentSigning() {
  const { signingToken } = useParams<{ signingToken: string }>();
  useDocumentTitle('Sign Document');
  const [signed, setSigned] = useState(false);

  const {
    data: context,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['magic-link-signing', signingToken],
    queryFn: () => SignaturesService.signaturesMagicLinkRetrieve({ token: signingToken! }),
    enabled: !!signingToken,
    retry: false,
    // Signing state must never render from cache: a revisit after signing
    // (or by another user in the same tab) has to show the current state.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  const signMutation = useMutation({
    mutationFn: (signedName: string) =>
      SignaturesService.signaturesMagicLinkCreate({
        token: signingToken!,
        requestBody: { signed_name: signedName, consent: true },
      }),
    onSuccess: () => setSigned(true),
  });

  if (!signingToken || isError) {
    return (
      <SigningShell>
        <CenteredMessage
          icon="fas fa-link-slash"
          title="This signing link is no longer valid"
          body="The link may have expired, been revoked, or already been used. Please contact the sender to request a new link."
        />
      </SigningShell>
    );
  }

  if (isLoading || !context) {
    return (
      <SigningShell>
        <div className="tw:flex tw:justify-center tw:py-10">
          <Spinner />
        </div>
      </SigningShell>
    );
  }

  if (signed) {
    return (
      <SigningShell>
        <CenteredMessage
          icon="fas fa-circle-check"
          title="Thank you — your signature has been recorded"
          body="You may now close this window. A copy of the fully executed document will be available once all parties have signed."
        />
      </SigningShell>
    );
  }

  const alreadySigned = context.already_signed;

  return (
    <SigningShell>
      <div className="tw:mb-4">
        <h1 className="tw:text-xl tw:font-semibold tw:text-zinc-900">
          Sign {context.document_label}
        </h1>
        <p className="tw:text-sm tw:text-zinc-600">
          Review the document below, then type your name to sign electronically.
        </p>
      </div>
      {signMutation.isError && (
        <div className="tw:mb-4 tw:rounded-md tw:border tw:border-stoplight-danger/40 tw:bg-stoplight-danger/10 tw:p-3 tw:text-sm tw:text-stoplight-danger">
          We couldn&apos;t record your signature. The link may have expired — please request a new
          one from the sender.
        </div>
      )}
      <SignatureSignBody
        documentUrl={context.document_url}
        documentLabel={context.document_label}
        isSubmitting={signMutation.isPending}
        onSign={(signedName) => signMutation.mutate(signedName)}
        alreadySigned={alreadySigned}
      />
    </SigningShell>
  );
}

export default PublicDocumentSigning;
